import React, { useState, useEffect, useRef } from 'react';

interface ScrambledEmailProps {
  email: string;
  speed?: number; // Animation speed in ms
}

const ScrambledEmail: React.FC<ScrambledEmailProps> = ({ email, speed = 12 }) => {
  const [displayString, setDisplayString] = useState<string>('');
  const [indices, setIndices] = useState<number[]>([]);
  const [isUnscrambling, setIsUnscrambling] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // scramble
  useEffect(() => {
    const chars = email.split('');
    const idx = Array.from({ length: chars.length }, (_, i) => i);

    // Modern Fisher-Yates shuffle for better randomness
    for (let i = idx.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [idx[i], idx[j]] = [idx[j], idx[i]];
    }

    setIndices(idx);
    setDisplayString(idx.map(i => chars[i]).join(''));

    return () => stopAnimation();
  }, [email]);

  const stopAnimation = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const bubbleSortStep = () => {
    setIndices((prevIndices) => {
      const newIndices = [...prevIndices];
      let swapped = false;

      // Find the first out-of-order pair and swap it (one swap per tick)
      for (let i = 0; i < newIndices.length - 1; i++) {
        if (newIndices[i] > newIndices[i + 1]) {
          [newIndices[i], newIndices[i + 1]] = [newIndices[i + 1], newIndices[i]];
          swapped = true;
          break; // Stop after one swap to create the animation effect
        }
      }

      // Update the visual string based on current index positions
      const nextString = newIndices.map(idx => email[idx]).join('');
      setDisplayString(nextString);

      if (!swapped) {
        stopAnimation();
        setIsUnscrambling(false);
      }

      return newIndices;
    });
  };

  const startUnscramble = (e: React.MouseEvent) => {
    e.preventDefault();
    if (isUnscrambling) return;
    
    setIsUnscrambling(true);
    intervalRef.current = setInterval(bubbleSortStep, speed);
  };

  return (
    <span>
      {displayString}{' '}
      {!isUnscrambling && displayString !== email && (
        <a 
          href="#" 
          onClick={startUnscramble} 
          style={{ cursor: 'pointer', textDecoration: 'underline', color: 'blue' }}
        >
          unscramble
        </a>
      )}
    </span>
  );
};

export default ScrambledEmail;